import React from 'react'
import {
  Typography,
  Snackbar,
  TableCell,
  TableBody,
  TableRow,
  Table,
  Box,
  TableHead,
  TableContainer,
  Paper,
  IconButton,
  Collapse,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import MuiAlert from '@mui/material/Alert'
import API from '@aws-amplify/api'
import { useDispatch } from 'react-redux'
import makeStyles from '@mui/styles/makeStyles';
import SmoothLoadingIndicator from 'components/shared/SmoothLoadingIndicator'
import { IProductRequest, ProductRequest } from 'interfaces/IProductRequest'
import { formatTimestamp } from 'shared/dateFormatter'

const useStyles = makeStyles((theme) => ({
  labelColumn: {
    maxWidth: 'min-content',
  },
  expandableDetails: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  hiddenDetails: {
    paddingTop: 0,
  },
  productRow: {
    cursor: 'pointer',
  },
}))

export default function ProductRequestList() {
  const dispatch = useDispatch()
  const classes = useStyles()

  const [productRequests, setProductRequests] = React.useState<
    IProductRequest[]
  >([])
  const [isLoading, setIsLoading] = React.useState<any>(true)
  const [snackbarState, setSnackbarState] = React.useState({
    isSuccessSnackbarOpen: false,
    isFailureSnackbarOpen: false,
    alertMessage: '',
  })

  const [expandedRows, setExpandedRows] = React.useState<{
    [key: string]: boolean
  }>({})

  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false)
  const [selectedRequest, setSelectedRequest] =
    React.useState<IProductRequest | null>(null)

  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Daftar Request STJ In/Out' })
  }, [dispatch])

  React.useEffect(() => {
    loadProductRequests()
  }, [])

  const getConfirmedQuantity = (
    productRequest: ProductRequest,
    productId: number | null
  ) => {
    if (productId == null) {
      return null
    }
    const product = productRequest.stockCard?.stockCardProducts.find(
      (product) => product.productId === productId
    )
    return product?.confirmedQuantity ?? null
  }

  const loadProductRequests = async () => {
    setIsLoading(true)
    let url = '/products/getProductRequests'
    const param = {
      queryStringParameters: { includeDetails: 'true' },
    }
    try {
      const response = await API.get('products', url, param)
      response.sort((a: IProductRequest, b: IProductRequest) => {
        const dateA = new Date(a.createdAt ?? 0)
        const dateB = new Date(b.createdAt ?? 0)
        return dateB.getTime() - dateA.getTime()
      })
      setProductRequests(response)
    } catch (error) {
      setSnackbarState((prevState) => ({
        ...prevState,
        alertMessage: `Gagal memuat order.`,
        isFailureSnackbarOpen: true,
      }))
      console.error(error)
    }
    setIsLoading(false)
  }

  const handleOpenCancelDialog = (req: IProductRequest) => {
    setSelectedRequest(req)
    setCancelDialogOpen(true)
  }

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false)
    setSelectedRequest(null)
  }

  const handleConfirmCancel = async () => {
    if (!selectedRequest) return
    try {
      setIsLoading(true)
      await API.post('products', '/products/cancelProductRequest', {
        body: {
          productRequestId: selectedRequest.productRequestId,
        },
      })
      setSnackbarState((prevState) => ({
        ...prevState,
        alertMessage: `Berhasil membatalkan request.`,
        isSuccessSnackbarOpen: true,
      }))
      await loadProductRequests()
    } catch (error) {
      console.error(error)
      setSnackbarState((prevState) => ({
        ...prevState,
        alertMessage: `Gagal membatalkan ${selectedRequest.productRequestDisplayId}`,
        isFailureSnackbarOpen: true,
      }))
    } finally {
      setIsLoading(false)
      handleCloseCancelDialog()
    }
  }

  const canCancel = (req: IProductRequest) => {
    return req.stockCard && !req.stockCard.isConfirmed
  }

  const getCancelReason = (req: IProductRequest) => {
    if (!req.stockCard) {
      return 'Tidak ada Kartu Stok terkait.'
    }
    if (req.stockCard.isConfirmed) {
      return 'Kartu Stok sudah dikonfirmasi.'
    }
    return null
  }

  return (
    <Box>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="none"></TableCell>
                <TableCell>Nomor Request Produk</TableCell>
                <TableCell>Tanggal Request</TableCell>
                <TableCell>Nomor Dokumen Kartu Stok Masuk/Keluar</TableCell>
                <TableCell>In/Out</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Grid container justifyContent="center" alignItems="center">
                      <SmoothLoadingIndicator size={40} />
                    </Grid>
                  </TableCell>
                </TableRow>
              )}
              {productRequests.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography>Tidak ada request produk </Typography>
                  </TableCell>
                </TableRow>
              )}
              {productRequests.map((productRequest) => (
                <React.Fragment key={productRequest.productRequestId}>
                  <TableRow
                    className={classes.productRow}
                    hover
                    onClick={() => {
                      setExpandedRows({
                        ...expandedRows,
                        [productRequest.productRequestId]:
                          !expandedRows[productRequest.productRequestId],
                      })
                    }}
                  >
                    <TableCell padding="none">
                      <IconButton aria-label="expand row" size="small">
                        {expandedRows[productRequest.productRequestId] ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {productRequest.productRequestDisplayId || '-'}
                    </TableCell>
                    <TableCell>
                      {formatTimestamp(
                        'id-ID',
                        productRequest.createdAt
                      )}
                    </TableCell>
                    <TableCell>
                      {productRequest.stockCard?.stockCardDisplayId ??
                        'Pending'}
                    </TableCell>
                    <TableCell>{productRequest.inOutDirection}</TableCell>
                    <TableCell>
                      {canCancel(productRequest) ? (
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenCancelDialog(productRequest)
                          }}
                        >
                          Batalkan
                        </Button>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {getCancelReason(productRequest)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className={
                        expandedRows[productRequest.productRequestId]
                          ? classes.hiddenDetails
                          : classes.expandableDetails
                      }
                    >
                      <Collapse
                        in={expandedRows[productRequest.productRequestId]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box>
                          <Grid container>
                            <Grid item xs={12}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">Kode Ruahan</Box>
                                    </TableCell>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">
                                        Kode Set Kemasan
                                      </Box>
                                    </TableCell>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">No Bets</Box>
                                    </TableCell>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">
                                        Filling Amount
                                      </Box>
                                    </TableCell>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">Qty Diminta</Box>
                                    </TableCell>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">
                                        Qty Dikeluarkan
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {productRequest.products?.map(
                                    (product, index) => (
                                      <TableRow
                                        key={
                                          product.productRequestProductId ??
                                          index
                                        }
                                      >
                                        <TableCell>
                                          {product.premixId}
                                        </TableCell>
                                        <TableCell>
                                          {product.packagingId}
                                        </TableCell>
                                        <TableCell>
                                          {product.batchNumber}
                                        </TableCell>
                                        <TableCell>
                                          {product.fillingAmount}
                                          {product.fillingUnit}
                                        </TableCell>
                                        <TableCell>{product.qty}</TableCell>
                                        <TableCell>
                                          {getConfirmedQuantity(
                                            productRequest,
                                            product.productId ?? null
                                          ) ?? 'Pending'}
                                        </TableCell>
                                      </TableRow>
                                    )
                                  )}
                                </TableBody>
                              </Table>
                            </Grid>
                          </Grid>
                          <Box mt={2}>
                            <Typography>
                              <Box fontWeight="bold" display="inline">
                                Notes:{' '}
                              </Box>
                              {productRequest.notes}
                            </Typography>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Konfirmasi Pembatalan Request</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Apakah anda yakin ingin membatalkan request berikut?
              </Typography>
              <Box mt={2} p={2} bgcolor="grey.100" borderRadius="4px">
                <Typography variant="body2">
                  <strong>Nomor Request:</strong>{' '}
                  {selectedRequest.productRequestDisplayId}
                </Typography>
                <Typography variant="body2">
                  <strong>Tanggal:</strong>{' '}
                  {formatTimestamp('id-ID', selectedRequest.createdAt)}
                </Typography>
                <Typography variant="body2">
                  <strong>Kartu Stok:</strong>{' '}
                  {selectedRequest.stockCard?.stockCardDisplayId ?? '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Arah:</strong> {selectedRequest.inOutDirection}
                </Typography>

                {selectedRequest.products &&
                  selectedRequest.products.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Detail Item:</strong>
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Kode Ruahan</TableCell>
                            <TableCell>Kode Set Kemasan</TableCell>
                            <TableCell>No Bets</TableCell>
                            <TableCell>Filling Amount</TableCell>
                            <TableCell>Qty Diminta</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedRequest.products.map((product, index) => (
                            <TableRow key={index}>
                              <TableCell>{product.premixId || '-'}</TableCell>
                              <TableCell>{product.packagingId || '-'}</TableCell>
                              <TableCell>{product.batchNumber || '-'}</TableCell>
                              <TableCell>
                                {product.fillingAmount
                                  ? `${product.fillingAmount} ${product.fillingUnit}`
                                  : '-'}
                              </TableCell>
                              <TableCell>{product.qty || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
              </Box>
              {getCancelReason(selectedRequest) && (
                <Box mt={2}>
                  <MuiAlert severity="warning">
                    Tidak dapat dibatalkan: {getCancelReason(selectedRequest)}
                  </MuiAlert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>
            Batal
          </Button>
          {selectedRequest && canCancel(selectedRequest) && (
            <Button
              onClick={handleConfirmCancel}
              color="secondary"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? 'Memproses...' : 'Konfirmasi Batalkan'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarState.isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbarState((prevState) => ({
            ...prevState,
            isSuccessSnackbarOpen: false,
          }))
        }
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {snackbarState.alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={snackbarState.isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbarState((prevState) => ({
            ...prevState,
            isFailureSnackbarOpen: false,
          }))
        }
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {snackbarState.alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

